import type { ChangeEvent } from 'react';

import { useTranslation } from 'react-i18next';

import { supportedLocales, type AppLocale } from '../app/config';

type HeaderProps = {
  currentLocale: AppLocale;
  dayLabel: string;
  searchQuery: string;
  timeLabel: string;
  onLocaleChange: (locale: AppLocale) => void;
  onOpenSidebar: () => void;
  onSearchChange: (value: string) => void;
};

export function Header({
  currentLocale,
  dayLabel,
  searchQuery,
  timeLabel,
  onLocaleChange,
  onOpenSidebar,
  onSearchChange,
}: HeaderProps) {
  const { t } = useTranslation();

  function handleSearchChange(event: ChangeEvent<HTMLInputElement>) {
    onSearchChange(event.target.value);
  }

  return (
    <header className='header'>
      <div className='branding'>
        <button
          className='mobile-menu-button'
          aria-label={t('common.openMenu')}
          onClick={onOpenSidebar}
          type='button'
        >
          <i className='fas fa-bars' aria-hidden='true' />
        </button>
        <img
          src='/img/dracula-tools.gif'
          alt='Dracula Tools'
          className='logo'
        />
        <h1 className='site-title'>
          {t('seo.siteTitle')}
          <span className='title-meta'>
            <span className='day-highlight'>{dayLabel}</span>
            <span className='clock-highlight'>{timeLabel}</span>
          </span>
        </h1>
      </div>

      <div className='header-actions'>
        <div className='search-container'>
          <input
            type='text'
            className='search-input'
            aria-label={t('common.searchLabel')}
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={t('common.searchPlaceholder')}
          />
          <i className='fas fa-search search-icon' aria-hidden='true' />
        </div>
        <div
          className='language-switcher'
          role='group'
          aria-label={t('common.language')}
        >
          {supportedLocales.map(locale => (
            <button
              key={locale}
              className={`language-button${locale === currentLocale ? ' active' : ''}`}
              onClick={() => onLocaleChange(locale)}
              type='button'
            >
              {locale.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
    </header>
  );
}
